import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, CalendarOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errorMessage } from "@/lib/api";
import { useDirectory, useMeta } from "@/hooks/useMeta";
import { fmtDateTime, toLocalInputValue } from "@/lib/format";
import { Btn, LabeledSelect } from "./Ui";
import { AvailabilityDayPicker, dateKey } from "./AvailabilityDayPicker";

const DURATIONS = [30, 45, 60, 90, 120];
const CUSTOM = "custom";

/**
 * Assign (create) or edit an appointment.
 *
 * Times come from the therapist's declared availability rather than a free
 * datetime field: a slot that is already booked, or outside their hours, cannot
 * be picked. The backend enforces the same two rules, with no override.
 */
export function AssignAppointmentDialog({ open, onOpenChange, submission, appointment, onSaved, defaultStart }) {
  const qc = useQueryClient();
  const { data: meta } = useMeta();
  const { data: directory } = useDirectory();
  const admins = useMemo(() => (directory || []).filter((u) => u.status === "active"), [directory]);
  const editing = !!appointment;

  const [adminId, setAdminId] = useState("");
  const [date, setDate] = useState("");
  const [startIso, setStartIso] = useState("");
  const [duration, setDuration] = useState(45);
  const [type, setType] = useState("intake_call");
  const [note, setNote] = useState("");
  const [attachIcs, setAttachIcs] = useState(true);
  const [durationMode, setDurationMode] = useState(45);
  const [confirmed, setConfirmed] = useState(true);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (appointment) {
      setAdminId(appointment.admin_id);
      setDate(dateKey(appointment.start));
      setStartIso(appointment.start);
      setDuration(appointment.duration_minutes || 45);
      setDurationMode(DURATIONS.includes(appointment.duration_minutes) ? appointment.duration_minutes : CUSTOM);
      setType(appointment.type || "intake_call");
      setNote(appointment.note || "");
      setConfirmed(appointment.confirmed !== false);
    } else {
      setAdminId(submission?.assigned_admin_id || admins.find((a) => a.role === "admin")?.id || admins[0]?.id || "");
      const base = defaultStart ? new Date(defaultStart) : new Date(Date.now() + 24 * 3600 * 1000);
      setDate(dateKey(base));
      setStartIso(defaultStart ? new Date(defaultStart).toISOString() : "");
      setDuration(45);
      setDurationMode(45);
      setType(submission?.service === "assessment" ? "assessment_session" : "intake_call");
      setNote("");
      setConfirmed(true);
    }
    setAttachIcs(true);
    setServerError(null);
  }, [open, appointment, submission, defaultStart, admins]);

  const { data: slotData, isFetching: loadingSlots } = useQuery({
    queryKey: ["availability-slots", adminId, date, duration, appointment?.id],
    queryFn: async () =>
      (
        await api.get(`/availability/${adminId}/slots`, {
          // No step_minutes: the server advances start times by the duration,
          // so the list always matches the length that is selected.
          params: { date, duration_minutes: duration, exclude_id: appointment?.id },
        })
      ).data,
    enabled: open && !!adminId && !!date,
  });

  const slots = slotData?.slots || [];
  const hasHours = (slotData?.blocks || []).length > 0;
  const selectedAdmin = admins.find((a) => a.id === adminId);

  // Drop a chosen slot that stops being valid after changing therapist/date/duration.
  useEffect(() => {
    if (!startIso) return;
    if (slotData && !slots.some((s) => s.start === startIso && s.available)) setStartIso("");
  }, [slotData]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        admin_id: adminId, start: startIso, duration_minutes: Number(duration),
        type, note, attach_ics: attachIcs, confirmed,
      };
      if (editing) return (await api.patch(`/appointments/${appointment.id}`, payload)).data;
      return (await api.post("/appointments", { ...payload, submission_id: submission.id })).data;
    },
    onSuccess: (data) => {
      toast.success(editing ? "Appointment updated" : `Appointment assigned - ${selectedAdmin?.name || "admin"} has been notified`);
      ["appointments", "submissions", "submission", "stats", "availability-slots"].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] })
      );
      onSaved?.(data);
      onOpenChange(false);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code) {
        setServerError(detail);
        qc.invalidateQueries({ queryKey: ["availability-slots"] });
      } else {
        setServerError(null);
        toast.error(errorMessage(err));
      }
    },
  });

  const canSave = adminId && startIso && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white p-0" data-testid="assign-appointment-dialog">
        <DialogHeader className="border-b border-border px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CalendarClock className="h-5 w-5 text-primary" /> {editing ? "Edit appointment" : "Assign appointment"}
          </DialogTitle>
          <DialogDescription>
            {submission ? (
              <>
                For <strong className="text-foreground">{submission.beneficiary_name}</strong> - {meta?.services?.[submission.service] || submission.service}
              </>
            ) : (
              <>
                For <strong className="text-foreground">{appointment?.beneficiary_name}</strong>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[65vh] gap-4 overflow-y-auto px-6 py-5">
          <LabeledSelect label="Assign to therapist" id="appt-admin" value={adminId} onChange={(e) => { setAdminId(e.target.value); setStartIso(""); setServerError(null); }} data-testid="assign-admin-select">
            <option value="">Select a therapist</option>
            {admins.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
                {a.specialty && meta?.specialties?.[a.specialty] ? ` - ${meta.specialties[a.specialty].label}` : ""}
                {a.role === "supervisor" ? " (supervisor)" : ""}
              </option>
            ))}
          </LabeledSelect>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <LabeledSelect
              label="Duration"
              id="appt-duration"
              value={durationMode}
              onChange={(e) => {
                const v = e.target.value;
                setDurationMode(v === CUSTOM ? CUSTOM : Number(v));
                if (v !== CUSTOM) setDuration(Number(v));
                setStartIso("");
              }}
              data-testid="assign-duration-select"
            >
              {DURATIONS.map((d) => (
                <option key={d} value={d}>{d} minutes</option>
              ))}
              <option value={CUSTOM}>Custom...</option>
            </LabeledSelect>
            {durationMode === CUSTOM && (
              <div>
                <label htmlFor="appt-duration-custom" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Custom duration (minutes)
                </label>
                <input
                  id="appt-duration-custom"
                  type="number"
                  min={15}
                  max={480}
                  step={5}
                  className="emerge-input !h-10 text-sm"
                  value={duration}
                  onChange={(e) => { setDuration(Number(e.target.value)); setStartIso(""); }}
                  data-testid="assign-duration-custom"
                />
                <p className="mt-1 text-xs text-muted-foreground">Between 15 and 480 minutes.</p>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Date
            </label>
            {adminId ? (
              <AvailabilityDayPicker
                adminId={adminId}
                value={date}
                onChange={(d) => { setDate(d); setStartIso(""); setServerError(null); }}
                durationMinutes={Number(duration) || 45}
                excludeId={appointment?.id}
              />
            ) : (
              <p className="rounded-[10px] border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                Choose a therapist to see their available days.
              </p>
            )}
          </div>

          {/* Available start times */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Available start times
            </label>
            {!adminId || !date ? (
              <p className="rounded-[10px] border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                Choose a therapist and a date.
              </p>
            ) : loadingSlots ? (
              <p className="flex items-center gap-2 px-1 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking availability...
              </p>
            ) : !hasHours ? (
              <div className="rounded-[10px] border border-[hsl(46_55%_80%)] bg-[hsl(46_90%_94%)] px-4 py-3 text-sm text-[hsl(30_70%_25%)]" data-testid="assign-no-availability">
                <p className="flex items-center gap-2 font-semibold">
                  <CalendarOff className="h-4 w-4" /> {selectedAdmin?.name || "This therapist"} is not available on this date
                </p>
                <p className="mt-1">
                  Pick another date, or{" "}
                  <Link to="/dashboard/availability" className="font-semibold underline" onClick={() => onOpenChange(false)}>
                    set their availability
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="assign-slot-grid">
                {slots.map((s) => {
                  const selected = s.start === startIso;
                  return (
                    <button
                      key={s.start}
                      type="button"
                      disabled={!s.available}
                      onClick={() => { setStartIso(s.start); setServerError(null); }}
                      title={s.available ? `Book ${s.local}` : `${s.local} is already booked`}
                      data-testid={`assign-slot-${s.local}`}
                      className={[
                        "rounded-[8px] border px-2 py-2 text-sm font-semibold transition",
                        selected
                          ? "border-primary bg-primary text-white"
                          : s.available
                            ? "border-border bg-white text-foreground hover:border-primary hover:text-primary"
                            : "cursor-not-allowed border-border bg-secondary/60 text-muted-foreground line-through",
                      ].join(" ")}
                    >
                      {s.local}
                    </button>
                  );
                })}
              </div>
            )}
            {hasHours && slots.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                Crossed-out times are already booked. Only free times inside {selectedAdmin?.name || "the therapist"}'s hours can be selected.
              </p>
            )}
          </div>

          <LabeledSelect label="Appointment type" id="appt-type" value={type} onChange={(e) => setType(e.target.value)} data-testid="assign-type-select">
            {Object.entries(meta?.appointment_types || {}).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </LabeledSelect>

          <div>
            <label htmlFor="appt-note" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Note (optional)
            </label>
            <textarea id="appt-note" rows={3} className="emerge-input !h-auto py-2 text-sm" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="Context for the therapist, e.g. parent prefers WhatsApp call" data-testid="assign-note-input" />
          </div>

          <div className="flex items-center justify-between rounded-[10px] border border-border bg-secondary/60 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Confirmed appointment</p>
              <p className="text-xs text-muted-foreground">
                Turn off to hold the slot but leave it on the waiting list until it is confirmed.
              </p>
            </div>
            <Switch checked={confirmed} onCheckedChange={setConfirmed} data-testid="assign-confirmed-switch" aria-label="Confirmed" />
          </div>

          <div className="flex items-center justify-between rounded-[10px] border border-border bg-secondary/60 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Attach .ics calendar file</p>
              <p className="text-xs text-muted-foreground">Included in the notification email so the therapist can add it to their calendar.</p>
            </div>
            <Switch checked={attachIcs} onCheckedChange={setAttachIcs} data-testid="assign-appointment-ics-switch" aria-label="Attach .ics" />
          </div>

          {serverError && (
            <div className="rounded-[10px] border border-destructive/40 bg-destructive/5 p-4" role="alert" data-testid="assign-conflict-alert">
              <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
                <AlertTriangle className="h-4 w-4" />
                {serverError.code === "overlap" ? "That time is already taken" : "Outside available hours"}
              </p>
              <p className="mt-1 text-sm text-foreground">{serverError.message}</p>
              {serverError.conflicts?.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm text-foreground">
                  {serverError.conflicts.map((c) => (
                    <li key={c.id}>
                      {fmtDateTime(c.start)} - {c.beneficiary_name} ({c.duration_minutes} min)
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Btn variant="secondary" onClick={() => onOpenChange(false)} data-testid="assign-cancel-button">
            Cancel
          </Btn>
          <Btn onClick={() => mutation.mutate()} disabled={!canSave} data-testid="assign-appointment-submit-button">
            {mutation.isPending && <Loader2 className="animate-spin" />}
            {editing ? "Save changes" : "Assign & notify"}
          </Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}
